import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, MapPin, Calendar } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const EAuctionPropertiesPage = () => {
    const backendBaseURL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001').replace('/api', '');
    const [loading, setLoading] = useState(true);
    const [properties, setProperties] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        location: '',
        type: 'Residential',
        reserve_price: '',
        emd_amount: '',
        auction_date: '',
        inspection_date: '',
        status: 'Active',
        display_order: 0,
        is_active: true,
        document: null
    });

    // Settings State
    const [settings, setSettings] = useState({
        section_title: '',
        section_description: '',
        section_note: ''
    });
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [settingsModalData, setSettingsModalData] = useState({
        section_title: '',
        section_description: ''
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchProperties = async () => {
        try {
            setLoading(true);
            const [propertiesResponse, settingsResponse] = await Promise.all([
                axios.get('/api/admin/e-auction-properties'),
                axios.get('/api/admin/e-auction-properties/settings')
            ]);

            if (propertiesResponse.data.success) {
                setProperties(propertiesResponse.data.data || []);
            }
            if (settingsResponse.data.success && settingsResponse.data.data) {
                setSettings(settingsResponse.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProperties();
    }, []);

    // Handlers
    const handleAddNew = () => {
        setEditingProperty(null);
        setModalData({
            title: '',
            location: '',
            type: 'Residential',
            reserve_price: '',
            emd_amount: '',
            auction_date: '',
            inspection_date: '',
            status: 'Active',
            display_order: properties.length + 1,
            is_active: true,
            document: null
        });
        setIsModalOpen(true);
    };

    const handleEdit = (property) => {
        setEditingProperty(property);

        let formattedDate = '';
        if (property.auction_date) {
            const d = new Date(property.auction_date);
            if (!isNaN(d.getTime())) {
                formattedDate = d.toISOString().split('T')[0];
            }
        }

        setModalData({
            title: property.title,
            location: property.location,
            type: property.type,
            reserve_price: property.reserve_price,
            emd_amount: property.emd_amount,
            auction_date: formattedDate,
            inspection_date: property.inspection_date,
            status: property.status,
            display_order: property.display_order,
            is_active: property.is_active === 1,
            document: null,
            existing_document: property.document_url
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/e-auction-properties/${deleteConfirm.id}`);
            toast.success('Property deleted successfully');
            fetchProperties();
        } catch (error) {
            console.error('Error deleting property:', error);
            toast.error('Failed to delete property');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            Object.keys(modalData).forEach(key => {
                if (key === 'document') {
                    if (modalData[key]) {
                        formData.append('document', modalData[key]);
                    }
                } else if (key !== 'existing_document') {
                    formData.append(key, modalData[key]);
                }
            });

            if (editingProperty) {
                await axios.put(`/api/admin/e-auction-properties/${editingProperty.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Property updated successfully');
            } else {
                await axios.post('/api/admin/e-auction-properties', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Property added successfully');
            }

            setIsModalOpen(false);
            fetchProperties();
        } catch (error) {
            console.error('Error saving property:', error);
            toast.error(error.response?.data?.message || 'Failed to save property');
        }
    };

    const toggleStatus = async (property) => {
        try {
            await axios.put(`/api/admin/e-auction-properties/${property.id}`, {
                ...property,
                is_active: !property.is_active
            });
            toast.success('Status updated');
            fetchProperties();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/e-auction-properties/settings', settings);
            toast.success('Section settings updated successfully');
            fetchProperties();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };



    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8 pb-10">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Property Listings</h1>
                    <p className="text-slate-500">Manage properties available for e-auction</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md"
                >
                    <Plus size={18} /> Add Property
                </button>
            </div>

            {/* Section Settings Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-slate-800">Section Header Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title || ''}
                            onChange={handleSettingsChange}
                            placeholder="e.g. Available Properties for Auction"
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle</label>
                        <input
                            type="text"
                            name="section_description"
                            value={settings.section_description || ''}
                            onChange={handleSettingsChange}
                            placeholder="Enter section description or subtitle"
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Note (Optional)</label>
                        <input
                            type="text"
                            name="section_note"
                            value={settings.section_note || ''}
                            onChange={handleSettingsChange}
                            placeholder="e.g. Property documents and inspection can be arranged upon request..."
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>





            {/* Properties Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((property) => (
                    <div key={property.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all group">

                        <div className="flex justify-between items-start mb-4">
                            <div className="bg-primary-50 text-primary-700 px-3 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider">
                                {property.type}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${property.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                {property.status}
                            </span>
                        </div>

                        <h3 className="font-bold text-slate-800 text-lg mb-2 line-clamp-2 min-h-[3.5rem]">{property.title}</h3>

                        <div className="space-y-2 mb-6">
                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                                <MapPin size={16} />
                                <span className="truncate">{property.location}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                                <span className="font-bold">₹</span>
                                <span>Reserve: ₹{property.reserve_price}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                                <Calendar size={16} />
                                <span>Auction: {property.auction_date ? new Date(property.auction_date).toLocaleDateString() : 'TBD'}</span>
                            </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                            <button
                                onClick={() => toggleStatus(property)}
                                className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${property.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                {property.is_active ? 'Active' : 'Inactive'}
                            </button>

                            <div className="flex gap-2">
                                <button onClick={() => handleEdit(property)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                    <Pencil size={16} />
                                </button>
                                <button onClick={() => handleDelete(property.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {properties.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-200">
                    <p className="text-slate-400 font-medium">No properties added yet. Click "Add Property" to get started.</p>
                </div>
            )}

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Property Title</label>
                                    <input
                                        type="text"
                                        value={modalData.title}
                                        onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. 2BHK Apartment in City Center"
                                        required
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Location</label>
                                    <input
                                        type="text"
                                        value={modalData.location}
                                        onChange={(e) => setModalData({ ...modalData, location: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. Sector 4, Noida"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Property Type</label>
                                    <select
                                        value={modalData.type}
                                        onChange={(e) => setModalData({ ...modalData, type: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    >
                                        <option value="Residential">Residential</option>
                                        <option value="Commercial">Commercial</option>
                                        <option value="Plot">Plot</option>
                                        <option value="Industrial">Industrial</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Auction Status</label>
                                    <select
                                        value={modalData.status}
                                        onChange={(e) => setModalData({ ...modalData, status: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Upcoming">Upcoming</option>
                                        <option value="Closed">Closed</option>
                                        <option value="Sold">Sold</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Reserve Price</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                                        <input
                                            type="text"
                                            value={modalData.reserve_price}
                                            onChange={(e) => setModalData({ ...modalData, reserve_price: e.target.value })}
                                            className="w-full pl-8 px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                            placeholder="e.g. 45,00,000"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">EMD Amount</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                                        <input
                                            type="text"
                                            value={modalData.emd_amount}
                                            onChange={(e) => setModalData({ ...modalData, emd_amount: e.target.value })}
                                            className="w-full pl-8 px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                            placeholder="e.g. 4,50,000"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Auction Date</label>
                                    <input
                                        type="date"
                                        value={modalData.auction_date}
                                        onChange={(e) => setModalData({ ...modalData, auction_date: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Inspection Date/Time</label>
                                    <input
                                        type="text"
                                        value={modalData.inspection_date}
                                        onChange={(e) => setModalData({ ...modalData, inspection_date: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. 10-12 Feb 2025"
                                    />
                                </div>

                                <div className="md:col-span-2 grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                        <input
                                            type="number"
                                            value={modalData.display_order}
                                            onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                    <div className="flex items-center pt-6">
                                        <label className="flex items-center gap-2 cursor-pointer select-none">
                                            <div className={`w-12 h-6 rounded-full p-1 transition-colors ${modalData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${modalData.is_active ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                <input
                                                    type="checkbox"
                                                    checked={modalData.is_active}
                                                    onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                                    className="hidden"
                                                />
                                            </div>
                                            <span className="font-semibold text-slate-700">Active Listing</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Property Document (PDF/Doc/Image)</label>
                                    <input
                                        type="file"
                                        onChange={(e) => setModalData({ ...modalData, document: e.target.files[0] })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                                    />
                                    {modalData.existing_document && !modalData.document && (
                                        <p className="mt-1 text-xs text-slate-500">
                                            Current: <a href={`${backendBaseURL}${modalData.existing_document}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">View Document</a>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg"
                                >
                                    Save Property
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Property"
                message="Are you sure you want to delete this property listing? This action cannot be undone."
            />


        </div>
    );
};

export default EAuctionPropertiesPage;
